import { Link } from 'react-router-dom'
import { Button } from '@gjl/ui'
import { SEO } from '@/components/SEO'
import { GlassCard } from '@/components/ui/glass-card'
import { useLuxeHero, useDirectionalReveal, useAlternatingReveal, useLuxeGrid } from '@/hooks'
import { ArrowRight, ExternalLink, Users, Layers, Heart, Clock, Music } from 'lucide-react'

const fugueLabProjects = [
  {
    slug: 'kaia-string-quartet',
    title: 'KAIA String Quartet',
    description:
      'Bold, brand-aligned site for a Chicago-based string quartet. Features concert calendar, embedded media, and email list integration.',
    image: '/portfolio/kaia-string-quartet.jpg',
    url: 'https://kaiastringquartet.com',
    tech: ['Squarespace', 'MailChimp', 'Vimeo/SoundCloud Embeds'],
  },
  {
    slug: 'jonathan-gregoire',
    title: 'Jonathan Gregoire – Creative Coach',
    description:
      'Animated, lead-focused site for a professional organist and creative coach. Includes testimonials, video embeds, and modern UI.',
    image: '/portfolio/jonathan-gregoire.jpg',
    url: 'https://jonathangregoire.com',
    tech: ['Next.js', 'ShadCN UI', 'Framer Motion', 'Vimeo Embeds'],
  },
  {
    slug: 'music-garrett-john-law',
    title: 'Garrett John Law – Organist & Conductor',
    description:
      'Personal musician site with CMS backend. Showcases media, biographical info, calendar, and performance archive.',
    image: '/portfolio/garrett.jpg',
    url: 'https://music.garrettjohnlaw.com',
    tech: ['Next.js', 'Tailwind CSS', 'ShadCN', 'Payload CMS', 'Vercel'],
  },
  {
    slug: 'patrick-pope',
    title: 'Dr. Patrick Pope – Composer',
    description:
      'Comprehensive portfolio for a liturgical composer. Features scores, audio previews, and detailed biography.',
    image: '/portfolio/patrick-pope.jpg',
    url: 'https://patrickpopeorganist.com',
    tech: ['Next.js', 'ShadCN UI', 'SoundCloud', 'Vimeo'],
  },
]

const whyMatters = [
  {
    icon: Users,
    title: 'Real-world software',
    description: 'Used by performers, ensembles, and churches.',
  },
  {
    icon: Layers,
    title: 'Product ownership',
    description: 'Full-stack delivery from design to deployment.',
  },
  {
    icon: Heart,
    title: 'User-centered design',
    description: 'Built for non-technical users and public audiences.',
  },
  {
    icon: Clock,
    title: 'Time-bounded',
    description: 'All work completed outside of full-time employment hours.',
  },
]

export function FugueLab() {
  // Hero entrance
  const heroRef = useLuxeHero<HTMLElement>(0.3)

  // Why matters: grid stagger
  const whyRef = useLuxeGrid<HTMLElement>('.why-card', {
    stagger: 0.08,
  })

  // Projects: alternating
  const projectsRef = useAlternatingReveal<HTMLElement>('.project-item', {
    offset: 60,
    blur: 4,
  })

  // Footer: fade
  const footerRef = useDirectionalReveal<HTMLElement>('.reveal', 'fade')

  return (
    <>
      <SEO
        title="Fugue Lab – Freelance Projects"
        description="Freelance software work for musicians and arts professionals. Real, live projects built to solve creative problems with clean, modern tech."
        url="https://software.garrettjohnlaw.com/fugue-lab"
      />

      {/* Hero Section */}
      <section ref={heroRef} className="relative overflow-hidden px-4 py-20 md:py-28">
        <div className="bg-gradient-radial-cyber absolute inset-0 opacity-30" />
        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <div className="mb-4 flex items-center justify-center gap-3">
            <Music className="text-primary h-8 w-8" />
            <p className="text-primary font-mono text-lg font-medium tracking-wide">&lt;fugue-lab /&gt;</p>
          </div>
          <h1 className="text-fluid-h1 text-foreground mb-6">Freelance Projects</h1>
          <p className="text-fluid-body-lg text-muted-foreground mx-auto max-w-2xl">
            Software for musicians and arts professionals. Real, live projects built to solve creative problems with
            clean, modern tech.
          </p>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Why This Matters */}
      <section ref={whyRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-fluid-h2 text-foreground mb-8 text-center">Why This Matters</h2>

          <div className="grid gap-4 sm:grid-cols-2">
            {whyMatters.map((item) => (
              <GlassCard key={item.title} hover className="why-card">
                <div className="flex items-start gap-3">
                  <div className="bg-primary/10 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                    <item.icon className="text-primary h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-foreground font-semibold">{item.title}</h3>
                    <p className="text-muted-foreground mt-1 text-sm">{item.description}</p>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Featured Projects */}
      <section ref={projectsRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-fluid-h2 text-foreground mb-10 text-center">Featured Projects</h2>

          <div className="space-y-8">
            {fugueLabProjects.map((project) => (
              <div key={project.slug} className="project-item">
                <GlassCard hover className="overflow-hidden">
                  <div className="flex flex-col gap-6 md:flex-row">
                    <div className="shrink-0 md:w-1/3">
                      <a href={project.url} target="_blank" rel="noopener noreferrer">
                        <div className="relative aspect-[4/3] overflow-hidden rounded-lg">
                          <img
                            src={project.image}
                            alt={project.title}
                            className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                          />
                          <div className="from-background/60 absolute inset-0 bg-gradient-to-t to-transparent" />
                        </div>
                      </a>
                    </div>
                    <div className="flex-1 space-y-3">
                      <h3 className="text-foreground text-xl font-semibold">
                        <a
                          href={project.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-primary inline-flex items-center gap-2 transition-colors"
                        >
                          {project.title}
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </h3>
                      <p className="text-muted-foreground">{project.description}</p>
                      <p className="text-primary font-mono text-xs">{project.tech.join(' · ')}</p>
                    </div>
                  </div>
                </GlassCard>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Footer CTA */}
      <section ref={footerRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-2xl">
          <GlassCard accent className="reveal py-10 text-center">
            <h2 className="text-fluid-h3 text-foreground mb-4">Interested in Working Together?</h2>
            <p className="text-muted-foreground mb-6">
              Whether you're a musician, ensemble, or arts organization, I'd love to hear about your project.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild className="gradient-cyber text-primary-foreground hover:glow-cyber-sm">
                <Link to="/contact">
                  Get In Touch
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="border-border hover:border-primary">
                <Link to="/">Back to Home</Link>
              </Button>
            </div>
          </GlassCard>
        </div>
      </section>
    </>
  )
}
